// 화면 그리기(§9.8) — 도구·색·굵기 정의(데이터). 도구 막대·편집기·렌더가 모두 여기만 읽는다.
// 도구를 더하려면 `TOOLS` 에 한 줄을 넣고 편집기(editor.ts)의 동작 표에 같은 이름을 넣는다.

/** 도구 이름. `control` = 그리기를 잠시 놓고 원격 조작으로(마우스가 다시 원격으로 간다). */
export type Tool =
  | 'control'
  | 'pen'
  | 'marker'
  | 'line'
  | 'arrow'
  | 'rect'
  | 'ellipse'
  | 'text'
  | 'laser'
  | 'select'
  | 'eraser';

export interface ToolDef {
  tool: Tool;
  label: string;
  icon: string;
  /** 단축키(`KeyboardEvent.key`, 소문자). 그리기가 켜진 동안만 — 원격으로 보내지 않는다. */
  key?: string;
}

/** 도구 막대 순서. */
export const TOOLS: readonly ToolDef[] = [
  { tool: 'control', label: '조작(원격 제어로 돌아가기)', icon: '🖱', key: 'escape' },
  { tool: 'pen', label: '펜', icon: '✏️', key: 'p' },
  { tool: 'marker', label: '형광펜', icon: '🖍', key: 'h' },
  { tool: 'line', label: '직선', icon: '╱', key: 'l' },
  { tool: 'arrow', label: '화살표', icon: '➚', key: 'a' },
  { tool: 'rect', label: '사각형', icon: '▭', key: 'r' },
  { tool: 'ellipse', label: '원', icon: '◯', key: 'o' },
  { tool: 'text', label: '글자', icon: 'T', key: 't' },
  { tool: 'laser', label: '레이저 포인터', icon: '🔴', key: 'k' },
  { tool: 'select', label: '선택·이동', icon: '⬚', key: 'v' },
  { tool: 'eraser', label: '지우개(선 단위)', icon: '⌫', key: 'e' },
];

/** 그리기 도구(문서에 객체를 만든다) — 편집기가 도구별 동작을 고를 때 쓴다. */
export const STROKE_TOOLS: ReadonlySet<Tool> = new Set(['pen', 'marker']);
export const SHAPE_TOOLS: ReadonlySet<Tool> = new Set(['line', 'arrow', 'rect', 'ellipse']);
/** 채우기를 쓸 수 있는 도형. */
export const FILLABLE_TOOLS: ReadonlySet<Tool> = new Set(['rect', 'ellipse']);

/** 도구별 마우스 모양(없으면 십자) — 뷰어 캔버스·원격지 그림판 공용. */
const TOOL_CURSORS: Partial<Record<Tool, string>> = {
  text: 'text',
  select: 'default',
  eraser: 'cell',
};
export function toolCursor(t: Tool): string {
  return TOOL_CURSORS[t] ?? 'crosshair';
}

/** 기본 색 8종(`#rrggbb` — 모델 검사 규칙과 같은 형식). 사용자 지정 색은 색 고르기로. */
export const COLORS: readonly { value: string; label: string }[] = [
  { value: '#ff3b30', label: '빨강' },
  { value: '#ff9500', label: '주황' },
  { value: '#ffcc00', label: '노랑' },
  { value: '#34c759', label: '초록' },
  { value: '#007aff', label: '파랑' },
  { value: '#af52de', label: '보라' },
  { value: '#000000', label: '검정' },
  { value: '#ffffff', label: '흰색' },
];

/** 굵기 3단계(기준 높이 1080 px). 형광펜은 여기에 `MARKER_WIDTH_SCALE` 을 곱한다. */
export const WIDTHS: readonly { value: number; label: string }[] = [
  { value: 3, label: '가늘게' },
  { value: 6, label: '보통' },
  { value: 12, label: '굵게' },
];

/** 글자 크기(기준 높이 px) — 굵기 단계에 맞춰 고른다(같은 단추로 조절). */
export const TEXT_SIZES: Readonly<Record<number, number>> = { 3: 24, 6: 36, 12: 56 };

export const MARKER_WIDTH_SCALE = 4;
/** 형광펜 불투명도 — 아래 글자가 비쳐 보이게. */
export const MARKER_ALPHA = 0.35;
/** 도형 채우기 불투명도. */
export const FILL_ALPHA = 0.25;

/** 레이저 포인터 꼬리가 사라지는 시간(ms) — 저장하지 않는 일시 표시(§9.8.3). */
export const LASER_FADE_MS = 1000;
export const LASER_COLOR = '#ff2d55';

/** 흰 판 배경색. */
export const BOARD_BG = '#ffffff';

/** 지우개·선택의 손떨림 여유(px). */
export const HIT_TOLERANCE_PX = 6;
/** 획 점 간격 하한(px) — 이보다 가까운 점은 버린다. */
export const STROKE_MIN_STEP_PX = 2;
/** 이보다 작게 끌면 도형을 만들지 않는다(클릭 실수). */
export const SHAPE_MIN_PX = 4;
/** 되돌리기 기록 상한. */
export const HISTORY_MAX = 200;

export const DEFAULT_STYLE = { color: COLORS[0].value, width: WIDTHS[1].value, fill: false } as const;
